import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCasaMapaPartnerAgencies } from "../../services/casamapaPartnerAgencyApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { casamapaAgencyInventoryPath } from "../../constants/casamapaAgencyPortal";
import { useLocale, useCurrency } from "../../intl";
import "../../styles/partnerAgenciesPage.css";

const SORT_OPTIONS = ["listings", "name", "sale", "rent"];

function partnerStatusLabel(status, t) {
  const key = String(status || "none");
  return t(`realEstate.casaMapa.admin.agencies.partnerStatus.${key}`, {}, key);
}

function partnerStatusTone(status) {
  const key = String(status || "none");
  if (key === "active" || key === "partner") return "partner";
  if (key === "pending") return "pending";
  return "listed";
}

function formatPriceRange(agency, formatMoney) {
  if (agency.priceMinCop == null && agency.priceMaxCop == null) return "—";
  const min = agency.priceMinCop != null ? formatMoney(agency.priceMinCop) : "—";
  const max = agency.priceMaxCop != null ? formatMoney(agency.priceMaxCop) : "—";
  if (min === max) return min;
  return `${min} – ${max}`;
}

function AgenciesSkeleton() {
  return (
    <div className="partner-agencies-page__skeleton-grid" aria-hidden="true">
      {[1, 2, 3, 4, 5, 6].map((id) => (
        <div key={id} className="partner-agencies-page__skeleton-card" />
      ))}
    </div>
  );
}

function AgencyCard({ agency, t, formatMoney, inventoryPath }) {
  const saleCount = agency.saleListingCount ?? 0;
  const rentCount = agency.rentListingCount ?? 0;
  const mixTotal = saleCount + rentCount;
  const salePct = mixTotal > 0 ? Math.round((saleCount / mixTotal) * 100) : 50;
  const rentPct = mixTotal > 0 ? 100 - salePct : 50;
  const sourceKeys = agency.sourceKeys || [];

  return (
    <article className="partner-agency-card">
      <div className="partner-agency-card__head">
        {agency.logoUrl ? (
          <img src={agency.logoUrl} alt="" className="partner-agency-card__logo" />
        ) : (
          <span className="partner-agency-card__mark" aria-hidden="true">
            {String(agency.name || "?").slice(0, 1)}
          </span>
        )}
        <div className="partner-agency-card__identity">
          <h2>{agency.name}</h2>
          <p className="partner-agency-card__slug">{agency.slug}</p>
        </div>
        <span className={`partner-agency-card__status partner-agency-card__status--${partnerStatusTone(agency.partnerStatus)}`}>
          {partnerStatusLabel(agency.partnerStatus, t)}
        </span>
      </div>

      {agency.description ? (
        <p className="partner-agency-card__desc">{agency.description}</p>
      ) : null}

      <dl className="partner-agency-card__metrics">
        <div className="partner-agency-card__metric">
          <dt>{t("realEstate.casaMapa.admin.agencies.activeListings")}</dt>
          <dd>{(agency.activeListingCount ?? 0).toLocaleString()}</dd>
        </div>
        <div className="partner-agency-card__metric">
          <dt>{t("realEstate.casaMapa.admin.agencies.priceRange")}</dt>
          <dd>{formatPriceRange(agency, formatMoney)}</dd>
        </div>
        <div className="partner-agency-card__metric">
          <dt>{t("realEstate.casaMapa.admin.listingType.sale")}</dt>
          <dd>{saleCount.toLocaleString()}</dd>
        </div>
        <div className="partner-agency-card__metric">
          <dt>{t("realEstate.casaMapa.admin.listingType.rent")}</dt>
          <dd>{rentCount.toLocaleString()}</dd>
        </div>
      </dl>

      {mixTotal > 0 ? (
        <div className="partner-agency-card__mix" aria-hidden="true">
          <div className="partner-agency-card__mix-labels">
            <span>
              {t("realEstate.casaMapa.admin.listingType.sale")} {salePct}%
            </span>
            <span>
              {t("realEstate.casaMapa.admin.listingType.rent")} {rentPct}%
            </span>
          </div>
          <div className="partner-agency-card__mix-track">
            <div className="partner-agency-card__mix-sale" style={{ width: `${salePct}%` }} />
            <div className="partner-agency-card__mix-rent" style={{ width: `${rentPct}%` }} />
          </div>
        </div>
      ) : null}

      {sourceKeys.length ? (
        <div className="partner-agency-card__sources">
          {sourceKeys.map((key) => (
            <span key={key} className="partner-agency-card__source-tag">
              {key}
            </span>
          ))}
        </div>
      ) : null}

      <div className="partner-agency-card__actions">
        <Link
          className="partner-dashboard-btn partner-dashboard-btn--primary"
          to={inventoryPath}
        >
          {t("realEstate.casaMapa.admin.agencies.reviewInventory")}
        </Link>
        {agency.profileUrl ? (
          <a className="partner-agency-card__link" href={agency.profileUrl} target="_blank" rel="noreferrer">
            {t("realEstate.casaMapa.admin.viewPublic")}
          </a>
        ) : null}
      </div>
    </article>
  );
}

export default function PartnerCasaMapaAgenciesPage() {
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("listings");

  const loadAgencies = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerAgencies({
        search: search.trim() || undefined,
      });
      setAgencies(result.agencies || []);
    } catch (err) {
      if (err.status === 403) {
        setError(t("realEstate.casaMapa.admin.errors.noCasaMapaAccess"));
      } else if (err.status === 401) {
        setError(t("realEstate.casaMapa.admin.errors.platformAdminSignIn"));
      } else if (err.status === 404) {
        setError(t("realEstate.casaMapa.admin.errors.agenciesRouteMissing"));
      } else {
        setError(err.message || t("realEstate.casaMapa.admin.errors.loadAgencies"));
      }
      setAgencies([]);
    } finally {
      setLoading(false);
    }
  }, [search, t]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchDraft.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const sortedAgencies = useMemo(() => {
    const rows = [...agencies];
    rows.sort((a, b) => {
      if (sortBy === "name") {
        return String(a.name || "").localeCompare(String(b.name || ""));
      }
      if (sortBy === "sale") {
        return (b.saleListingCount || 0) - (a.saleListingCount || 0);
      }
      if (sortBy === "rent") {
        return (b.rentListingCount || 0) - (a.rentListingCount || 0);
      }
      return (b.activeListingCount || 0) - (a.activeListingCount || 0);
    });
    return rows;
  }, [agencies, sortBy]);

  const totals = useMemo(
    () =>
      agencies.reduce(
        (acc, agency) => ({
          listings: acc.listings + (agency.activeListingCount || 0),
          sale: acc.sale + (agency.saleListingCount || 0),
          rent: acc.rent + (agency.rentListingCount || 0),
        }),
        { listings: 0, sale: 0, rent: 0 }
      ),
    [agencies]
  );

  const hasActiveSearch = Boolean(searchDraft.trim());

  return (
    <section className="partner-agencies-page">
      <header className="partner-agencies-page__hero partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</p>
          <h2>{t("realEstate.casaMapa.admin.agencies.title")}</h2>
          <p className="partner-dashboard-hero__intro">
            {t("realEstate.casaMapa.admin.partnerDashboard.yourAgenciesHint")}
          </p>
        </div>
      </header>

      {!loading && agencies.length ? (
        <div className="partner-kpi-grid" aria-label={t("realEstate.casaMapa.admin.agencies.statsLabel")}>
          <article className="partner-kpi-card partner-kpi-card--slate">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              🏢
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.agencies.countLabel")}</span>
            <strong className="partner-kpi-card__value">{agencies.length}</strong>
          </article>
          <article className="partner-kpi-card partner-kpi-card--blue">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              🏘️
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.agencies.activeListings")}</span>
            <strong className="partner-kpi-card__value">{totals.listings.toLocaleString()}</strong>
          </article>
          <article className="partner-kpi-card partner-kpi-card--green">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              🔑
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.listingType.sale")}</span>
            <strong className="partner-kpi-card__value">{totals.sale.toLocaleString()}</strong>
          </article>
          <article className="partner-kpi-card partner-kpi-card--amber">
            <span className="partner-kpi-card__icon" aria-hidden="true">
              📋
            </span>
            <span className="partner-kpi-card__label">{t("realEstate.casaMapa.admin.listingType.rent")}</span>
            <strong className="partner-kpi-card__value">{totals.rent.toLocaleString()}</strong>
          </article>
        </div>
      ) : null}

      <div className="partner-agencies-page__toolbar">
        <div className="partner-agencies-page__search">
          <span className="partner-agencies-page__search-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            type="search"
            placeholder={t("realEstate.casaMapa.admin.agencies.searchPlaceholder")}
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            aria-label={t("realEstate.casaMapa.admin.agencies.searchPlaceholder")}
          />
        </div>
        <select
          className="partner-agencies-page__sort"
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          aria-label={t("realEstate.casaMapa.admin.agencies.sortLabel")}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(`realEstate.casaMapa.admin.agencies.sort.${option}`)}
            </option>
          ))}
        </select>
        {hasActiveSearch ? (
          <button
            type="button"
            className="partner-agencies-page__toolbar-btn"
            onClick={() => setSearchDraft("")}
          >
            {t("realEstate.casaMapa.admin.listings.clearFilter")}
          </button>
        ) : null}
        <button type="button" className="partner-agencies-page__toolbar-btn" onClick={loadAgencies}>
          {t("realEstate.casaMapa.admin.refresh")}
        </button>
      </div>

      {error ? (
        <div className="partner-agencies-page__error" role="alert">
          <p>{error}</p>
          <div className="partner-agencies-page__error-actions">
            <button type="button" className="partner-dashboard-btn" onClick={loadAgencies}>
              {t("realEstate.casaMapa.admin.agencies.retry")}
            </button>
            <Link className="partner-agency-card__link" to={path("login")}>
              {t("realEstate.casaMapa.admin.errors.signInDifferentAccount")}
            </Link>
          </div>
        </div>
      ) : null}

      {loading ? <AgenciesSkeleton /> : null}

      {!loading && !error && sortedAgencies.length ? (
        <>
          <p className="partner-agencies-page__results">
            {sortedAgencies.length} {t("realEstate.casaMapa.admin.agencies.countLabel")}
            {hasActiveSearch ? ` · "${searchDraft.trim()}"` : ""}
          </p>
          <div className="partner-agencies-page__grid">
            {sortedAgencies.map((agency) => (
              <AgencyCard
                key={agency.slug}
                agency={agency}
                t={t}
                formatMoney={formatMoney}
                inventoryPath={casamapaAgencyInventoryPath(agency.slug)}
              />
            ))}
          </div>
        </>
      ) : null}

      {!loading && !error && !sortedAgencies.length ? (
        <div className="partner-agencies-page__empty">
          <span className="partner-agencies-page__empty-icon" aria-hidden="true">
            🏢
          </span>
          <h2>{t("realEstate.casaMapa.admin.agencies.empty")}</h2>
          {hasActiveSearch ? (
            <p>{t("realEstate.casaMapa.admin.agencies.searchPlaceholder")}</p>
          ) : null}
          {hasActiveSearch ? (
            <button
              type="button"
              className="partner-dashboard-btn"
              onClick={() => setSearchDraft("")}
            >
              {t("realEstate.casaMapa.admin.listings.clearFilter")}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
